import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SalesPackageGridComponent } from './components/sales-package-grid/sales-package-grid.component';
import {
  FilterModalComponent,
  FilterCategory,
  FilterOption,
} from '../shared/components/filter-modal/filter-modal.component';
import {
  StatsCardComponent,
  StatsCardData,
} from '../shared/components/stats-card/stats-card.component';
import { SearchbarComponent } from '../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../shared/components/action-button/action-button.component';
import { EmptyResultComponent } from '../shared/components/empty-result/empty-result.component';
import { NotificationData } from '../services/notification.service';
import { ModalStateService } from '../services/modal-state.service';
import { PackageService } from '../services/package.service';
import { AuthService } from '../services/auth.service';
import { SalesService } from '../services/sales.service';
import { Subject, of } from 'rxjs';
import { takeUntil, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Package } from '../shared/types';

/** Shared icon style for sales stat cards (Today's Revenue, Sold Today, Sold All Time). */
const SALES_CARD_ICON_STYLE = {
  position: 'absolute' as const,
  right: '-20px',
  top: '-20px',
  width: '140px',
  height: '98px',
  zIndex: 3,
  transform: 'rotate(-15deg)',
  opacity: 0.8,
};

/** Build the three-item salesStats array from resolved value strings. Pass '$0.00' / '0' as defaults. */
function buildSalesStats(
  todayRevenue: string,
  soldToday: string | number,
  soldAllTime: string | number
): StatsCardData[] {
  return [
    {
      title: "Today's Revenue",
      value: todayRevenue,
      icon: '/assets/images/sales/todays-revenue-icon.svg',
      color: 'white',
      valueColor: '#73A0E9',
      iconStyle: SALES_CARD_ICON_STYLE,
    },
    {
      title: 'Sold Today',
      value: soldToday,
      icon: '/assets/images/sales/sold-today-icon.svg',
      color: 'white',
      valueColor: '#988AF4',
      iconStyle: SALES_CARD_ICON_STYLE,
    },
    {
      title: 'Sold All Time',
      value: soldAllTime,
      icon: '/assets/images/sales/sold-all-time-icon.svg',
      color: 'white',
      valueColor: '#51A8D9',
      iconStyle: SALES_CARD_ICON_STYLE,
    },
  ];
}

/**
 * SalesComponent - Manages and displays car wash packages for staff, including searching, filtering, sorting (via API), showing sales statistics, and initiating order creation through a modal.
 * @component
 * @selector app-sales
 * @example
 *   <app-sales></app-sales>
 *
 * Purpose:
 * - Loads active car wash packages for staff and maintains both full and filtered lists.
 * - Provides search with debounced input, a shared filter modal to filter by car wash type and sort options (sorting is performed server-side), and UI state for an "Add Order" modal.
 * - Fetches and formats sales statistics for display in stats cards and falls back to sensible defaults on error.
 *
 * Public inputs/outputs:
 * @input {none} none - This standalone component does not accept external @Input properties; it manages its own data via injected services.
 * @output {none} none - The component does not expose @Output EventEmitters; interactions (like order creation) are handled by modal child components and internal handlers.
 *
 * Key public/state properties:
 * - packages: Package[] - full list of active packages loaded from the API.
 * - filteredPackages: Package[] - packages after applying search and filter state.
 * - loading: boolean - indicates package/stat loading state.
 * - searchText: string - current search term.
 * - isFilterModalVisible: boolean - whether the shared filter modal is open.
 * - filterCategories: FilterCategory[] - configuration for the filter modal.
 * - currentFilters: { carWashTypes: string[]; sortBy: string } - currently applied filters.
 * - salesStats: StatsCardData[] - formatted sales statistics for display.
 * - showAddOrderModal: boolean / selectedPackageForOrder: Package | null - state for opening and populating the add-order modal.
 *
 * Key methods and responsibilities:
 * - ngOnInit(): void
 *   * Lifecycle init: calls loadPackages(), initializeSalesStats(), initializeFilterCategories(), and setupSearchDebouncing().
 * - ngOnDestroy(): void
 *   * Cleans up subscriptions via destroy$.
 * - loadPackages(sortBy?: string): void
 *   * Loads packages from the PackageService, filters to active packages, applies client-side filters/search, and handles loading/error states.
 * - initializeSalesStats(): void
 *   * Loads sales statistics via SalesService, builds card data, and falls back to defaults if the API errors.
 * - setDefaultSalesStats(): void
 *   * Populates salesStats with default zeroed values as a safe fallback.
 * - formatCurrency(value: string): string / formatCurrencyWithoutSymbol(value: string): string
 *   * Helpers to format numeric strings into currency display formats.
 * - initializeFilterCategories(): void
 *   * Prepares filter category and option definitions used by the modal (car wash types and sort options).
 * - onSelectPackage(packageData: Package): void
 *   * Opens the notification modal (Buy Package) with the selected package pre-selected.
 * - trackByPackageId(index: number, packageData: Package): string|number
 *   * Optimizes ngFor rendering by returning a stable identifier.
 * - setupSearchDebouncing(): void
 *   * Debounces user input and calls performSearch after a delay.
 * - onSearchInput(searchTerm: string) / clearSearch(): void
 *   * Manage searchText and push terms into the debounced search pipeline.
 * - performSearch(searchTerm: string): void
 *   * Filters packages by name, features, or wash types (case-insensitive).
 * - showFilterModal() / closeFilterModal(): void
 *   * Toggle filter modal visibility.
 * - onFiltersApplied(categories: FilterCategory[]): void
 *   * Converts modal category selections into currentFilters, triggers a reload via loadPackages(sortBy) if sorting is chosen, and closes modal.
 * - onFiltersCleared(): void
 *   * Resets currentFilters and reloads packages without sort; the modal stays open (CWS-509).
 * - applyFilters(): void
 *   * Applies search and car wash type filters to produce filteredPackages (note: sorting handled by API).
 * - sortPackages(packages: Package[], sortBy: string): Package[]
 *   * Client-side sorting utility (kept for fallback/local sorting scenarios).
 * - hasActiveFilters(): boolean / getActiveFilterCount(): number
 *   * Helpers to detect and count active filters for UI indicators.
 * - onCloseAddOrder(): void / onContinueAddOrder(payload: { tierId: string; optionId: string }): void
 *   * Handlers for closing or continuing from the add-order modal (continuation defers to the modal for full order flow).
 *
 * Notes and important behaviors:
 * - Sorting preferences are applied server-side by passing a sortBy argument to loadPackages; client-side sorting exists but is no longer the primary method.
 * - All subscriptions are protected by takeUntil(this.destroy$) to prevent memory leaks.
 * - The component expects an authenticated user (AuthService.user()); if no user is present, package loading aborts with an error log.
 */
@Component({
  selector: 'app-sales',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SalesPackageGridComponent,
    FilterModalComponent,
    StatsCardComponent,
    SearchbarComponent,
    ActionButtonComponent,
    EmptyResultComponent,
  ],
  templateUrl: './sales.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sales.component.scss'],
})
export class SalesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  packages: Package[] = [];
  filteredPackages: Package[] = [];
  loading = true;
  searchText = '';

  // Filter state
  isFilterModalVisible = false;
  filterCategories: FilterCategory[] = [];
  currentFilters: {
    carWashTypes: string[];
    sortBy: string;
  } = {
    carWashTypes: [],
    sortBy: '',
  };

  // Sales statistics
  salesStats: StatsCardData[] = [];

  constructor(
    private packageService: PackageService,
    private authService: AuthService,
    private salesService: SalesService,
    private modalStateService: ModalStateService
  ) {}

  ngOnInit(): void {
    this.loadPackages();
    this.initializeSalesStats();
    this.initializeFilterCategories();
    this.setupSearchDebouncing();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads car wash packages for the current staff user, filters active packages, applies client-side filters, and updates component state while handling loading and errors.
   * @example
   * loadPackages('price')
   * undefined
   * @param {{string}} {{sortBy}} - Optional sort key to order packages (e.g., 'price' or 'name').
   * @returns {{void}} No return value; updates component properties and handles side effects.
   **/
  private loadPackages(sortBy?: string): void {
    this.loading = true;

    // Ensure user session is available before making API calls
    const currentUser = this.authService.user();

    if (!currentUser) {
      console.error('No user found');
      this.loading = false;
      return;
    }

    this.packageService
      .getCarWashPackagesForStaff(sortBy)
      .pipe(
        catchError(error => {
          console.error('Error loading packages:', error);
          this.loading = false;
          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: packages => {
          console.log('Sales component received packages:', packages);
          // Filter to only show active packages (package_status = true)
          const activePackages = packages.filter(pkg => pkg.package_status === true);
          console.log('Active packages:', activePackages);
          this.packages = activePackages;
          // Apply filters after packages are loaded (for car wash types and search)
          this.applyFilters();
          this.loading = false;
        },
        error: error => {
          console.error('Error loading packages:', error);
          this.loading = false;
        },
      });
  }

  /**
   * Initialize sales statistics with real API data
   */
  private initializeSalesStats(): void {
    this.salesService
      .getSalesStatistics()
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error loading sales statistics:', error);
          // Fallback to default values if API fails
          this.setDefaultSalesStats();
          return of(null);
        })
      )
      .subscribe({
        next: response => {
          if (response && response.status && response.data) {
            const stats = response.data;
            this.salesStats = buildSalesStats(
              this.formatCurrency(stats.total_revenue_today),
              stats.washes_today,
              stats.washes_till_today
            );
            console.log('Sales stats created:', this.salesStats);
          } else {
            this.setDefaultSalesStats();
          }
        },
        error: error => {
          console.error('Error loading sales statistics:', error);
          this.setDefaultSalesStats();
        },
      });
  }

  /**
   * Set default sales statistics as fallback
   */
  private setDefaultSalesStats(): void {
    this.salesStats = buildSalesStats('$0.00', '0', '0');
    console.log('Default sales stats created:', this.salesStats);
  }

  /**
   * Format currency value
   * @param value - String value to format
   * @returns Formatted currency string
   */
  private formatCurrency(value: string): string {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '$0';
    return `$${numValue.toFixed(2)}`;
  }

  /**
   * Format currency value without dollar sign
   * @param value - String value to format
   * @returns Formatted currency string without symbol
   */
  private formatCurrencyWithoutSymbol(value: string): string {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '0';
    return numValue.toFixed(2);
  }

  /**
   * Initialize filter categories for the shared filter modal
   */
  private initializeFilterCategories(): void {
    // Initialize car wash types
    const carWashTypeOptions: FilterOption[] = [
      {
        id: 'monthly_membership',
        label: 'Monthly Membership',
        value: 'monthly_membership',
        selected: false,
      },
      { id: 'single_wash', label: 'Single Wash', value: 'single_wash', selected: false },
    ];

    // Initialize sort options
    const sortOptions: FilterOption[] = [
      {
        id: 'list-price-high-low',
        label: 'List Price High to Low',
        value: 'list-price-high-low',
        selected: false,
      },
      {
        id: 'list-price-low-high',
        label: 'List Price Low to High',
        value: 'list-price-low-high',
        selected: false,
      },
      {
        id: 'strikeoff-price-high-low',
        label: 'Strike-off Price High to Low',
        value: 'strikeoff-price-high-low',
        selected: false,
      },
      {
        id: 'strikeoff-price-low-high',
        label: 'Strike-off Price Low to High',
        value: 'strikeoff-price-low-high',
        selected: false,
      },
      {
        id: 'family-plan-high-low',
        label: 'Family Plan Price High to Low',
        value: 'family-plan-high-low',
        selected: false,
      },
      {
        id: 'family-plan-low-high',
        label: 'Family Plan Price Low to High',
        value: 'family-plan-low-high',
        selected: false,
      },
      {
        id: 'recently-updated',
        label: 'Recently Updated',
        value: 'recently-updated',
        selected: false,
      },
      {
        id: 'earliest-updated',
        label: 'Earliest Updated',
        value: 'earliest-updated',
        selected: false,
      },
    ];

    // Initialize filter categories
    this.filterCategories = [
      { id: 'carWashTypes', name: 'Car Wash Type', type: 'checkbox', options: carWashTypeOptions },
      { id: 'sortBy', name: 'Sort By', type: 'radio', options: sortOptions },
    ];
  }

  /**
   * Opens the notification modal (Buy Package flow) with the selected package pre-selected.
   */
  onSelectPackage(packageData: Package): void {
    const washType = this.resolvePrimaryWashType(packageData);
    const notification: NotificationData = {
      type: 'create-order-request',
      message: 'Buy Package',
      timestamp: new Date(),
      data: {
        packageId: packageData.id,
        package_id: packageData.id,
        package_name: packageData.package_name,
        wash_type: washType,
        washType,
        isBuyPackageFlow: true,
      },
    };
    this.modalStateService.showModal(notification, false);
  }

  /** Prefer single wash when a package supports multiple wash types (matches sales grid cards). */
  private resolvePrimaryWashType(pkg: Package): string | undefined {
    const types = pkg.wash_types || [];
    if (!types.length) {
      return undefined;
    }
    if (types.length === 1) {
      return types[0];
    }
    const lower = types.map(t => String(t).toLowerCase());
    const singleIdx = lower.indexOf('single_wash');
    if (singleIdx >= 0) {
      return types[singleIdx];
    }
    const monthlyIdx = lower.findIndex(t => t.includes('monthly'));
    if (monthlyIdx >= 0) {
      return types[monthlyIdx];
    }
    return types[0];
  }

  /**
   * TrackBy function for package list optimization
   * @param index - Item index
   * @param packageData - Package data
   * @returns Unique identifier for tracking
   */
  trackByPackageId(index: number, packageData: Package): string | number {
    return packageData.id || index;
  }

  // Search functionality
  private setupSearchDebouncing(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(searchTerm => {
        this.performSearch(searchTerm);
      });
  }

  onSearchInput(searchTerm: string): void {
    this.searchText = searchTerm.trim();
    this.searchSubject.next(this.searchText);
  }

  clearSearch(): void {
    this.searchText = '';
    this.searchSubject.next('');
  }

  /**
   * Filters the component's packages into filteredPackages based on a search term matching package_name, features, or wash_types (case-insensitive); resets to all packages when searchTerm is falsy.
   * @example
   * performSearch('express')
   * // this.filteredPackages updated to only packages whose name, features, or wash_types include 'express'
   * @param {string} searchTerm - Search term used to filter packages (case-insensitive).
   * @returns {void} Updates this.filteredPackages; does not return a value.
   **/
  private performSearch(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredPackages = [...this.packages];
      return;
    }

    this.filteredPackages = this.packages.filter(
      pkg =>
        pkg.package_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.features?.join(' ').toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.wash_types?.join(' ').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // Filter functionality
  showFilterModal(): void {
    this.isFilterModalVisible = true;
  }

  closeFilterModal(): void {
    this.isFilterModalVisible = false;
  }

  /**
   * Apply selected filter categories to update the component's current filters, reload packages (with sort if selected), and close the filter modal.
   * @example
   * nFiltersApplied([{ id: 'carWashTypes', options: [{ value: 'basic', selected: true }] }, { id: 'sortBy', options: [{ value: 'price_asc', selected: true }] }])
   * undefined
   * @param {{FilterCategory[]}} {{categories}} - The array of filter category objects containing options and selection state.
   * @returns {{void}} No return value; updates component state, triggers package reload, and closes the filter modal.
   **/
  onFiltersApplied(categories: FilterCategory[]): void {
    // Convert filter categories back to the expected format
    const carWashTypeCategory = categories.find(cat => cat.id === 'carWashTypes');
    const sortCategory = categories.find(cat => cat.id === 'sortBy');

    const newSortBy = sortCategory?.options.find(opt => opt.selected)?.value || '';
    const newCarWashTypes =
      carWashTypeCategory?.options.filter(opt => opt.selected).map(opt => opt.value) || [];

    this.currentFilters = {
      carWashTypes: newCarWashTypes,
      sortBy: newSortBy,
    };

    // Reload packages with sort parameter if sort is selected
    // Only one sort can be done at a time via API
    // applyFilters() will be called automatically after packages are loaded
    if (newSortBy) {
      this.loadPackages(newSortBy);
    } else {
      // If no sort, reload without sort
      this.loadPackages();
    }

    this.closeFilterModal();
  }

  /**
   * Filters the component's packages by search text and selected car wash types and updates this.filteredPackages.
   * @example
   * applyFilters()
   * undefined
   * @param {{void}} {{none}} - No arguments.
   * @returns {{void}} Updates this.filteredPackages with the filtered results and returns nothing.
   **/
  private applyFilters(): void {
    let filtered = [...this.packages];

    // Apply search filter
    if (this.searchText) {
      filtered = filtered.filter(
        pkg =>
          pkg.package_name?.toLowerCase().includes(this.searchText.toLowerCase()) ||
          pkg.features?.join(' ').toLowerCase().includes(this.searchText.toLowerCase()) ||
          pkg.wash_types?.join(' ').toLowerCase().includes(this.searchText.toLowerCase())
      );
    }

    // Apply other filters (car wash types)
    if (this.currentFilters.carWashTypes?.length > 0) {
      filtered = filtered.filter(pkg =>
        pkg.wash_types?.some(washType => this.currentFilters.carWashTypes.includes(washType))
      );
    }

    // Note: Sorting is now handled by the API via loadPackages() method
    // No need for client-side sorting anymore

    this.filteredPackages = filtered;
  }

  /**
   * Sorts an array of Package objects according to the provided sort key and returns a new sorted array.
   * @example
   * sortPackages(samplePackages, 'list-price-high-low')
   * [ /* sorted Package objects */
  private sortPackages(packages: Package[], sortBy: string): Package[] {
    const sorted = [...packages];

    switch (sortBy) {
      case 'list-price-high-low':
        return sorted.sort(
          (a, b) => parseFloat(b.single_list_price || '0') - parseFloat(a.single_list_price || '0')
        );
      case 'list-price-low-high':
        return sorted.sort(
          (a, b) => parseFloat(a.single_list_price || '0') - parseFloat(b.single_list_price || '0')
        );
      case 'strikeoff-price-high-low':
        return sorted.sort(
          (a, b) =>
            parseFloat(b.single_strikeoff_price || '0') -
            parseFloat(a.single_strikeoff_price || '0')
        );
      case 'strikeoff-price-low-high':
        return sorted.sort(
          (a, b) =>
            parseFloat(a.single_strikeoff_price || '0') -
            parseFloat(b.single_strikeoff_price || '0')
        );
      case 'family-plan-high-low':
        return sorted.sort(
          (a, b) =>
            parseFloat(b.monthly_list_price || '0') - parseFloat(a.monthly_list_price || '0')
        );
      case 'family-plan-low-high':
        return sorted.sort(
          (a, b) =>
            parseFloat(a.monthly_list_price || '0') - parseFloat(b.monthly_list_price || '0')
        );
      case 'recently-updated':
        return sorted.sort(
          (a, b) => new Date(b.updated_at || '').getTime() - new Date(a.updated_at || '').getTime()
        );
      case 'earliest-updated':
        return sorted.sort(
          (a, b) => new Date(a.updated_at || '').getTime() - new Date(b.updated_at || '').getTime()
        );
      default:
        return sorted;
    }
  }

  hasActiveFilters(): boolean {
    return (
      (this.currentFilters.carWashTypes?.length ?? 0) > 0 || Boolean(this.currentFilters.sortBy)
    );
  }

  getActiveFilterCount(): number {
    let count = 0;
    if ((this.currentFilters.carWashTypes?.length ?? 0) > 0) count += 1;
    if (this.currentFilters.sortBy) count += 1;
    return count;
  }

  /**
   * Handle add order modal close
   */
  onCloseAddOrder(): void {
    // No longer used, NotificationModal handles its own close
  }

  /**
   * Handle add order modal continue
   * @param payload - Order creation payload
   */
  onContinueAddOrder(_payload: { tierId: string; optionId: string }): void {
    // No longer used, NotificationModal handles its own flow
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
